import { useState } from 'react';
import { t, Trans } from '@lingui/macro';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import Table from '@decisiv/ui-components/lib/components/Table';
import { Column } from '@decisiv/ui-components/lib/components/Table/types';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import Loading from 'components/Loading';
import useSort from 'utils/useSort';

import { useCurrentUserContext } from 'utils/User';
import PaginationFooter from 'components/PaginationFooter';
import { parseDate } from 'utils/dates';
import ActionsCell from 'pages/Teams/Cells/ActionsCell';
import NameCell from 'pages/Teams/Cells/NameCell';
import PlatformAccountsCell from 'pages/Teams/Cells/PlatformAccountsCell';
import TeamModalProvider from 'pages/Teams/CreateTeamModal/contexts/TeamModalContext';
import CreateTeamModal from 'pages/Teams/CreateTeamModal';
import { RightAlignedFlex } from '../components/styles';
import { useManagedSentinelTeamsQuery } from './hooks/useManagedSentinelTeamsQuery';

type ManagedTeamsType =
  ManagedSentinelTeamsQuery['managedSentinelTeams']['collection'][0];
type ColumnProperties = Pick<
  ManagedTeamsType,
  | 'id'
  | 'name'
  | 'totals'
  | 'enabled'
  | 'createdAt'
  | 'teamKey'
  | 'apiUsername'
  | 'platformAccounts'
  | 'scheduledForDeletionAt'
>;

const Teams = () => {
  const { isSuperUser } = useCurrentUserContext();

  const [page, setPage] = useState(1);

  const [{ sort }, { getSortProps }] = useSort();
  const { defaultSort, onSort } = getSortProps();

  const { data, loading } = useManagedSentinelTeamsQuery({
    variables: {
      sort,
      page,
    },
  });

  const columns: Column<ColumnProperties>[] = [
    {
      name: 'name',
      title: t`Name`,
      DataCell: NameCell,
      sortBy: (direction) => onSort('NAME', direction),
    },
    {
      name: 'id',
      title: t`ID`,
      DataCell: ({ rowData: { id } }) => <span>{id}</span>,
      sortBy: (direction) => onSort('ID', direction),
    },
    {
      name: 'assets',
      title: t`Assets`,
      DataCell: ({ rowData: { totals } }) => (
        <span>{totals?.assetGroupings?.enabledAssets}</span>
      ),
      sortBy: (direction) => onSort('ENABLED_ASSET_COUNT', direction),
    },
    {
      name: 'members',
      title: t`Members`,
      DataCell: ({ rowData: { totals } }) => (
        <span>{totals?.members?.total}</span>
      ),
    },
    {
      name: 'createdAt',
      title: t`Created`,
      DataCell: ({ rowData: { createdAt } }) => (
        <span>{parseDate(createdAt)}</span>
      ),
      sortBy: (direction) => onSort('CREATED_AT', direction),
    },
    {
      name: 'teamKey',
      title: t`Key`,
      DataCell: ({ rowData: { teamKey } }) => <span>{teamKey}</span>,
      sortBy: (direction) => onSort('TEAM_KEY', direction),
    },
    {
      name: 'portalId',
      title: t`Portal ID`,
      DataCell: PlatformAccountsCell,
      sortBy: (direction) => onSort('DEALER_GROUP_ID', direction),
    },
    {
      name: 'apiUsername',
      title: t`API User`,
      DataCell: ({ rowData: { apiUsername } }) => (
        <span>{apiUsername ?? '-'}</span>
      ),
      sortBy: (direction) => onSort('API_USER', direction),
    },
    {
      name: 'enabled',
      title: t`Status`,
      DataCell: ActionsCell,
    },
  ];

  if (loading) return <Loading />;

  const teams = data?.managedSentinelTeams?.collection ?? [];
  const pagination = data?.managedSentinelTeams?.pagination;

  return (
    <>
      <Flex marginX={2} marginY={1} justifyContent="space-between">
        {isSuperUser && (
          <TeamModalProvider>
            <CreateTeamModal />
          </TeamModalProvider>
        )}
        <RightAlignedFlex alignItems="center">
          <P size="small" as="span" shade={1}>
            <Trans>Search Teams by Name</Trans>
          </P>
          <SearchInput name="searchByTeam" size="small" value="" />
        </RightAlignedFlex>
      </Flex>
      <Table
        data={teams}
        columns={columns}
        onSort={onSort}
        defaultSort={defaultSort}
        footer={() => (
          <PaginationFooter
            title={t`Teams`}
            setCurrentPage={setPage}
            totalOnPage={teams?.length}
            page={pagination?.page}
            perPage={pagination?.perPage}
            totalEntries={pagination?.totalEntries}
            totalPages={pagination?.totalPages}
          />
        )}
      />
    </>
  );
};

export default Teams;
